import { MERCHANT_TERMS, TRIP_TERMS } from '@field-support/shared';
import type { ar } from './ar';
import { adminEn } from './admin.en';
import { analyticsEn } from './analytics.en';
import { catalogEn } from './catalog.en';
import { formsEn } from './forms.en';
import { importEn } from './import.en';
import { trackingEn } from './tracking.en';

/** English (secondary). Must cover exactly the same keys as Arabic. */
export const en: typeof ar = {
  dateRange: {
    label: 'Range',
    today: 'Today',
    last7: 'Last 7 days',
    last14: 'Last 14 days',
    thisMonth: 'This month',
    custom: 'Custom',
  },
  common: {
    clearFilters: 'Clear filters',
    appName: 'The Engine',
    language: 'العربية',
    loading: 'Loading…',
    forbidden: 'This account has no permission to view this page.',
    notFound: 'Page not found.',
    welcome: 'Welcome. Pick a page from the navigation.',
    logout: 'Sign out',
    save: 'Save',
    saving: 'Saving…',
    saved: 'Saved',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    close: 'Close',
    search: 'Search',
    clearSearch: 'Clear search',
    showPassword: 'Show password',
    hidePassword: 'Hide password',
    retry: 'Retry',
    actions: 'Actions',
    selectAll: 'Select all rows',
    selectRow: 'Select row',
    status: 'Status',
    name: 'Name',
    code: 'Code',
    previous: 'Previous',
    next: 'Next',
    page: 'Page',
    items: 'items',
    noData: 'No records.',
    errorTitle: 'Request failed',
    active: 'Active',
    brand: `${MERCHANT_TERMS.en.one}`,
    branch: 'Branch',
    primaryNav: 'Main navigation',
    agent: 'Agent',
    date: 'Date',
    from: 'From',
    to: 'To',
    minutesUnit: 'min',
    optionalNote: 'Note (optional)',
    yes: 'Yes',
    no: 'No',
    exportCsv: 'Export CSV',
    exportXlsx: 'Export Excel',
    all: 'All',
    refresh: 'Refresh',
    metersUnit: 'm',
    note: 'Note',
    details: 'Details',
    copied: 'Copied',
    noFileChosen: 'No file chosen',
    confirm: 'Confirm',
    undo: 'Undo',
    myLocation: 'Go to my location',
    myLocationFailed: 'Could not get your location. Allow location access in the browser and try again.',
    notNow: 'Not now',
    locationOffTitle: 'Location access is off',
    locationOffBody:
      'Turn on location so the map opens where you are: click the icon at the left of the address bar, set Location to Allow, then choose Retry.',
    locationStillBlocked: 'Still blocked. If you just allowed location, reload the page (Safari applies the change only after a reload).',
    reloadPage: 'Reload page',
  },
  shell: {
    pin: 'Pin the menu',
    unpin: 'Unpin the menu',
    menu: 'Menu',
  },
  nav: {
    overview: 'Overview',
    dashboard: 'Performance',
    anomalies: 'GPS anomalies',
    syncIssues: 'Sync issues',
    configuration: 'Configuration',
    catalog: 'Catalog',
    administration: 'Administration',
    visits: 'Visits',
    assignments: 'Assigned visits',
    brands: `${MERCHANT_TERMS.en.many}`,
    branches: 'Branches',
    users: 'Users & permissions',
    devices: 'Device alerts',
    forms: 'Form builder',
    import: 'Bulk import',
    travel: 'Time between visits',
    activity: 'Shifts and kilometres',
    liveMap: 'Live map',
    route: 'Daily route',
    zoho: 'Zoho Desk',
    geography: 'Zones and areas',
    locations: 'Branch locations',
    alerts: 'Unlisted branches',
    notifications: 'Notifications',
    settings: 'Mobile app',
  },
  login: {
    title: 'Sign in to The Engine',
    phone: 'Mobile number',
    phoneHint: '01XXXXXXXXX',
    password: 'Password',
    submit: 'Sign in',
    signingIn: 'Signing in…',
  },
  visits: {
    emptyFiltered: 'No visits match these filters.',
    emptyNone: 'No visits in this date range yet. They appear here once agents submit them.',
    title: 'Visits',
    description:
      'One row per project visit. A trip to one branch that covered several projects has one row per project; the trip and branch columns repeat, so the export can be pivoted.',
    statusCount: 'project visits in this range',
    statusCount_one: 'project visit in this range',
    statusCount_two: 'project visits in this range',
    statusCount_few: 'project visits in this range',
    tripRef: `${TRIP_TERMS.en.trip}`,
    projectOf: 'Project {{n}} of {{total}}',
    mainVisit: `${TRIP_TERMS.en.mainVisit}`,
    additionalVisit: `${TRIP_TERMS.en.additionalVisit}`,
    project: 'Project',
    service: 'Service',
    issue: 'Issue',
    photos: 'Photos',
    noPhotos: 'No photos.',
    fileName: 'File name on the ticket',
    ticket: 'Ticket',
    duration: 'Duration',
    typed: 'not in the list',
    detailTitle: 'Trip',
    submissionId: 'Submission ID',
    anomalyScore: 'Anomaly score',
    lastError: 'Last error',
    allAgents: 'All agents',
    allProjects: 'All projects',
    allServices: 'All services',
    allIssueTypes: 'All issue types',
    allIssues: 'All issues',
    anyIssue: 'Any',
    withIssue: 'With an issue',
    withoutIssue: 'No issue',
    position: 'Project visit',
    projectKey: 'Project key',
    visitType: 'Visit type',
    serviceDetail: 'Service detail',
    issueFound: 'Issue found',
    issueType: 'Issue type',
    issues: 'Issues',
    comment: 'Comment',
    filter: {
      projectKey: 'Project',
      serviceKey: 'Service',
      issueTypeKey: 'Issue type',
      issueKey: 'Issue',
      issueFound: 'Issue found',
    },
  },
  assignments: {
    showCard: 'Show',
    title: 'Assigned visits',
    description:
      'Plan the day for your team: which branches each agent visits, for which projects, and what is expected. The agent accepts and starts the visit from the app.',
    statusDone: 'assigned visits completed',
    new: 'Assign visits',
    newTitle: 'Assign visits',
    view: 'View',
    planTab: 'Day plan',
    reportTab: 'Follow-up',
    dragHint:
      'Drag a card onto another agent to reassign it (while not started). Reassigning and cancelling are recorded in the audit log.',
    counts: {
      assigned: 'Assigned',
      accepted: 'Accepted',
      inProgress: 'In progress',
      completed: 'Completed',
      notStarted: 'Not started',
      cancelled: 'Cancelled',
    },
    agent: 'Agent',
    priority: 'Priority',
    date: 'Date',
    time: 'Time',
    timeHint: 'Optional',
    branches: 'Branches',
    allMerchants: 'All merchants',
    area: 'Area',
    allAreas: 'All areas',
    searchBranches: 'Search branches',
    projects: 'Projects',
    projectsHint:
      'Optional. For each project, the visit type and service you expect, where you know them. The agent can change them; differences are reported.',
    project: 'Project',
    expectedVisitType: 'Expected visit type',
    expectedService: 'Expected service',
    notSpecified: 'Not specified',
    addProject: 'Add project',
    removeProject: 'Remove',
    instruction: 'What the visit is for',
    // English uses _zero, _one and _other; _two, _few and _many exist because en mirrors ar's shape.
    assignCount_zero: 'Assign {{count}} branches',
    assignCount_one: 'Assign {{count}} branch',
    assignCount_two: 'Assign {{count}} branches',
    assignCount_few: 'Assign {{count}} branches',
    assignCount_many: 'Assign {{count}} branches',
    assignCount_other: 'Assign {{count}} branches',
    created_zero: 'No visits assigned',
    created_one: '{{count}} visit assigned',
    created_two: '{{count}} visits assigned',
    created_few: '{{count}} visits assigned',
    created_many: '{{count}} visits assigned',
    created_other: '{{count}} visits assigned',
    reassign: 'Reassign',
    movedTo: 'Moved to {{name}}',
    movedBack: 'Moved back to {{name}}',
    edit: 'Edit',
    editTitle: 'Edit visit',
    editNotStartedOnly: 'You can edit the visit until the agent starts it. A new agent gets it as assigned and has to accept it again.',
    cancel: 'Cancel assignment',
    cancelled: 'Assignment cancelled',
    cancelReason: 'Reason for cancelling',
    noTeam: 'No agents in your team. An administrator sets the team manager of each agent on the Users page.',
    nothingPlanned: 'Nothing planned for this day.',
    reference: 'Reference',
    acceptedAt: 'Accepted',
    startedAt: 'Started',
    completedAt: 'Completed',
    ticket: 'Ticket',
    deviations: 'Plan vs reality',
    deviated: 'Different from the plan',
    acceptToStart: 'Accept to start',
    startToComplete: 'Start to completion',
    byAgentDay: 'Per agent per day',
    deviationsTitle: 'Where the visit differed from the plan',
    deviationsHint:
      'Branch changed, a planned project not visited, a project added, or a visit type or service different from the one expected.',
    notStartedTitle: 'Not started by {{date}}',
    notStartedHint: 'Assigned or accepted, and nobody has begun the visit: the follow-up list.',
  },
  assignArea: {
    toggle: 'Assign a whole area',
    toggleHint: 'Choose an area: every active branch in it is listed, ticked. Untick the ones to leave out, then confirm.',
    chooseArea: 'Area',
    loading: "Loading the area's branches…",
    alreadyAssigned: 'Already assigned to this agent on this day',
    notLocated: 'No verified location',
    selected: '{{count}} of {{total}} branches selected',
    truncated: '{{count}} more branches in this area did not fit in one action. Split the area or assign the rest separately.',
    truncated_one: '{{count}} more branch in this area did not fit in one action. Split the area or assign the rest separately.',
    truncated_two: '{{count}} more branches in this area did not fit in one action. Split the area or assign the rest separately.',
    truncated_few: '{{count}} more branches in this area did not fit in one action. Split the area or assign the rest separately.',
  },
  bulk: {
    selectionRegion: 'Selected rows',
    selected: 'selected',
    clear: 'Clear selection',
    delete: 'Delete selected',
    confirm: 'Delete {{count}}',
    listSeparator: ', ',
    andMore: '{{names}} and {{count}} more',
    unknownRow: 'Unknown row',
    willSkip: '{{count}} will be skipped:',
    nothingToDo: 'None of the selected rows can be deleted.',
    resultTitle: 'Bulk delete finished',
    resultSummary: 'Deleted: {{deleted}} · Deactivated: {{deactivated}} · Skipped: {{skipped}} · Failed: {{failed}}',
    doneToast: 'Deleted {{deleted}}, deactivated {{deactivated}}',
    allDone: 'Every selected row was handled.',
    outcome: { DELETED: 'Deleted', DEACTIVATED: 'Deactivated', SKIPPED: 'Skipped', FAILED: 'Failed' },
    reason: {
      HAS_HISTORY: 'Has visit history, so it is deactivated instead of deleted',
      ACCOUNT_REMOVED: 'Removed and signed out; visit history kept',
      NOT_FOUND: 'Not found, or already deleted',
      OWN_ACCOUNT: 'You cannot delete your own account',
      SUPER_ADMIN_ONLY: 'Only a super admin can delete a super admin',
      LAST_SUPER_ADMIN: 'The last active super admin cannot be deleted',
      UNEXPECTED: 'Unexpected error',
    },
    brands: {
      confirmTitle: `Delete {{count}} ${MERCHANT_TERMS.en.manyLower}?`,
      confirmTitle_one: `Delete {{count}} ${MERCHANT_TERMS.en.oneLower}?`,
      confirmTitle_two: `Delete {{count}} ${MERCHANT_TERMS.en.manyLower}?`,
      confirmTitle_few: `Delete {{count}} ${MERCHANT_TERMS.en.manyLower}?`,
      willDelete: '{{count}} with no visit history will be deleted permanently, with their branches:',
      willDelete_one: '{{count}} with no visit history will be deleted permanently, with its branches:',
      willDelete_two: '{{count}} with no visit history will be deleted permanently, with their branches:',
      willDelete_few: '{{count}} with no visit history will be deleted permanently, with their branches:',
      willDeactivate: '{{count}} with visit history will be deactivated instead, so their visits are kept:',
      willDeactivate_one: '{{count}} with visit history will be deactivated instead, so its visits are kept:',
      willDeactivate_two: '{{count}} with visit history will be deactivated instead, so their visits are kept:',
      willDeactivate_few: '{{count}} with visit history will be deactivated instead, so their visits are kept:',
      branchesAffected: '{{count}} branches will be deleted or deactivated along with them.',
      branchesAffected_one: '{{count}} branch will be deleted or deactivated along with them.',
      branchesAffected_two: '{{count}} branches will be deleted or deactivated along with them.',
      branchesAffected_few: '{{count}} branches will be deleted or deactivated along with them.',
    },
    branches: {
      confirmTitle: 'Delete {{count}} branches?',
      confirmTitle_one: 'Delete {{count}} branch?',
      confirmTitle_two: 'Delete {{count}} branches?',
      confirmTitle_few: 'Delete {{count}} branches?',
      willDelete: '{{count}} with no visit history will be deleted permanently:',
      willDelete_one: '{{count}} with no visit history will be deleted permanently:',
      willDelete_two: '{{count}} with no visit history will be deleted permanently:',
      willDelete_few: '{{count}} with no visit history will be deleted permanently:',
      willDeactivate: '{{count}} with visit history will be deactivated instead, so their visits are kept:',
      willDeactivate_one: '{{count}} with visit history will be deactivated instead, so its visits are kept:',
      willDeactivate_two: '{{count}} with visit history will be deactivated instead, so their visits are kept:',
      willDeactivate_few: '{{count}} with visit history will be deactivated instead, so their visits are kept:',
    },
    users: {
      confirmTitle: 'Delete {{count}} users?',
      confirmTitle_one: 'Delete {{count}} user?',
      confirmTitle_two: 'Delete {{count}} users?',
      confirmTitle_few: 'Delete {{count}} users?',
      willDelete: '{{count}} will be removed and signed out. Their visit history is kept:',
      willDelete_one: '{{count}} will be removed and signed out. Their visit history is kept:',
      willDelete_two: '{{count}} will be removed and signed out. Their visit history is kept:',
      willDelete_few: '{{count}} will be removed and signed out. Their visit history is kept:',
      willDeactivate: '{{count}} will be deactivated:',
      willDeactivate_one: '{{count}} will be deactivated:',
      willDeactivate_two: '{{count}} will be deactivated:',
      willDeactivate_few: '{{count}} will be deactivated:',
    },
  },
  users: {
    emptyFiltered: 'No users match these filters.',
    emptyNone: 'No users yet.',
    title: 'Users & permissions',
    description: 'Accounts for The Engine and the field app, with role defaults and per-user overrides.',
    statusCount: 'users',
    statusCount_one: 'user',
    statusCount_two: 'users',
    statusCount_few: 'users',
    newUser: 'New user',
    fullName: 'Full name',
    phone: 'Mobile number',
    role: 'Role',
    allRoles: 'All roles',
    allStates: 'All states',
    password: 'Password',
    newPassword: 'New password (leave empty to keep)',
    passwordPolicy: 'At least 10 characters with letters and digits.',
    lastLogin: 'Last sign-in',
    permissions: 'Effective permissions',
    permissionColumn: 'Permission',
    overrides: 'Permission overrides',
    manager: 'Team manager',
    managerHint: 'Plans this user’s visits (with the “Assign visits” permission).',
    noManager: 'No team',
    roleDefault: 'Role default',
    override: 'Override',
    effective: 'Effective',
    inherit: 'Follows role',
    grant: 'Granted',
    revoke: 'Revoked',
    overrideCount_zero: 'No overrides',
    overrideCount_one: '+{{count}} override',
    overrideCount_two: '+{{count}} overrides',
    overrideCount_few: '+{{count}} overrides',
    overrideCount_many: '+{{count}} overrides',
    overrideCount_other: '+{{count}} overrides',
    deactivate: 'Deactivate',
    activate: 'Activate',
    deleted: 'User deleted (kept in history)',
    deactivateTitle: 'Deactivate {{name}}?',
    deactivateConsequence:
      'They are signed out of The Engine and the field app at once, and cannot sign in until the account is activated again.',
    deactivatedToast: '{{name}} deactivated',
    reactivatedToast: '{{name}} is active again',
    deleteTitle: 'Delete {{name}}?',
    deleteConsequence: 'The account is removed and signed out everywhere. Their visit history is kept. This cannot be undone.',
    searchPlaceholder: 'Name or mobile number',
    roles: {
      SUPER_ADMIN: 'Super admin',
      OPERATIONS_MANAGER: 'Operations & catalog',
      FORM_DESIGNER: 'Form designer',
      SUPERVISOR: 'Field supervisor',
      FIELD_AGENT: 'Field agent',
    },
    permission: {
      manageBrands: `Manage ${MERCHANT_TERMS.en.manyLower}`,
      manageBranches: 'Manage branches & locations',
      setGeofenceRadius: 'Set geofence radius',
      bulkImportCatalog: 'Bulk import catalog',
      reviewUnlistedBranches: 'Review unlisted branches',
      manageForms: 'Design forms',
      manageUsers: 'Manage users',
      viewAnalytics: 'View performance',
      viewAnomalies: 'View GPS anomalies',
      assignVisits: 'Assign visits to their team',
      manageZoho: 'Configure Zoho',
      manageAppDistribution: 'Manage app distribution',
      mobileCheckin: 'Use the field app',
      viewAgentLocation: 'View agent location',
      sendNotifications: 'Send notifications',
    },
  },
  devices: {
    title: 'New device alerts',
    description:
      'A sign-in from a phone other than the one the account is bound to. The device id is created by the app, not read from the hardware: reinstalling the app or resetting the phone gives a new one, so a legitimate agent can trigger this. While pending, the agent keeps working.',
    devicesOf: 'Phones of {{name}}',
    manage: 'Phones',
    requireApproval: 'Require approval for new phones',
    requireApprovalHint:
      'On: a new phone is pending until approved (it still works). Off: it is allowed at once; the alert is still raised.',
    approve: 'Approve',
    reject: 'Reject',
    dismiss: 'Dismiss alert',
    rejectConfirm: 'Rejecting signs this phone out and blocks it for this account until approved. Continue?',
    rejectNote: 'Reason (optional)',
    first: 'First phone',
    model: 'Phone',
    appVersion: 'App',
    firstSeen: 'First seen',
    lastSeen: 'Last seen',
    deviceId: 'Device id',
    statuses: { APPROVED: 'Approved', PENDING: 'Pending', REJECTED: 'Rejected' },
    open: 'Open',
    resolved: 'Resolved',
    noAlerts: 'No open alerts.',
  },
  errors: {
    NETWORK_ERROR: 'Cannot reach the server from this address. Check the connection and the server settings.',
    ORIGIN_NOT_ALLOWED: 'Cannot connect to the server from this address. Check the server settings.',
    UNAUTHORIZED: 'The session has ended. Sign in again.',
    FORBIDDEN: 'This account has no permission for this action.',
    NOT_FOUND: 'Not found.',
    CONFLICT: 'Conflicts with existing data.',
    RATE_LIMITED: 'Too many attempts. Wait, then try again.',
    VALIDATION_ERROR: 'Some of the data was not accepted. Check it, then try again.', // new — needs native review
    checkFields: 'Check the highlighted fields.',
    PAYLOAD_TOO_LARGE: 'The file or data is too large.',
    UNSUPPORTED_MEDIA_TYPE: 'This file type is not accepted.',
    PRECONDITION_FAILED: 'The data changed meanwhile. Reload, then try again.',
    ZOHO_NOT_CONFIGURED: 'Zoho Desk is not configured.',
    ZOHO_ERROR: 'Zoho Desk returned an error.',
    INTERNAL_ERROR: 'Unexpected server error.',
    unknown: 'Something went wrong. Error code: {{code}}',
    codeLabel: 'Error code:',
    reason: {
      INVALID_CREDENTIALS: 'Wrong phone number or password.',
      ACCOUNT_INACTIVE: 'This account is deactivated. Contact an administrator.',
      NO_ENGINE_ACCESS: 'This account cannot sign in to the admin panel.',
      TOKEN_EXPIRED: 'The session has ended. Sign in again.',
      TOKEN_INVALID: 'The session has ended. Sign in again.',
      SESSION_REVOKED: 'This session was signed out. Sign in again.',
      // User management (USER_ADMIN_REASONS); the permission reasons list {{permissions}}.
      SUPER_ADMIN_ONLY_ASSIGNS_SUPER_ADMIN: 'Only a super admin can give the super admin role.', // new — needs native review
      CANNOT_GRANT_UNHELD_PERMISSIONS: 'You cannot grant permissions you do not have yourself: {{permissions}}.', // new — needs native review
      EXCEEDS_OWN_PERMISSIONS: 'This role and its overrides would go beyond your own permissions: {{permissions}}.', // new — needs native review
      CANNOT_DEACTIVATE_SELF: 'You cannot deactivate your own account.', // new — needs native review
      CANNOT_EDIT_SELF_ROLE: 'You cannot change your own role or permissions.', // new — needs native review
      SUPER_ADMIN_ONLY_MODIFIES_SUPER_ADMIN: 'Only a super admin can change a super admin.', // new — needs native review
      CANNOT_DELETE_SELF: 'You cannot delete your own account.', // new — needs native review
      SUPER_ADMIN_ONLY_DELETES_SUPER_ADMIN: 'Only a super admin can delete a super admin.', // new — needs native review
      LAST_SUPER_ADMIN: 'The last active super admin cannot be demoted, deactivated or deleted.', // new — needs native review
      OWN_MANAGER: 'A user cannot be their own team manager.', // new — needs native review
      MANAGER_LOOP: 'This manager is in this user’s own team. Choose someone else.', // new — needs native review
      PHONE_IN_USE: 'Another account already has this mobile number.', // new — needs native review
      PHONE_OF_DELETED_USER: 'A deleted account has this mobile number.', // new — needs native review
      // APK upload and publish (APK_UPLOAD_REASONS).
      APK_NOT_MULTIPART: 'The file was not sent as an upload. Try again from this page.', // new — needs native review
      APK_FILE_MISSING: 'No APK file arrived. Choose the file and try again.', // new — needs native review
      APK_TRUNCATED: 'The file is too small to be an APK.', // new — needs native review
      APK_NOT_A_ZIP: 'This file is not an APK.', // new — needs native review
      APK_NO_CENTRAL_DIRECTORY: 'The APK is incomplete or corrupted. Upload it again.', // new — needs native review
      APK_NO_ANDROID_MANIFEST: 'This is not an Android app: the file has no AndroidManifest.xml.', // new — needs native review
      APK_TOO_LARGE: 'The APK is larger than the {{maxMb}} limit.', // new — needs native review
      APK_DEV_BUILD_NOT_PUBLISHABLE: 'This is a development build (it needs Metro) and cannot be published. Upload a release build.', // new — needs native review
      // Zoho (ZOHO_FAILURE_REASONS): the test ticket's error, the connection test, the last test, alerts.
      ZOHO_NOT_CONFIGURED: 'Zoho Desk is not fully set up: check the credentials, the connection, the org and the department.', // new — needs native review
      ZOHO_TIMEOUT: 'Zoho Desk did not answer in time. Try again.', // new — needs native review
      ZOHO_NETWORK: 'The server cannot reach Zoho Desk. Check the server’s connection.', // new — needs native review
      ZOHO_AUTH_REJECTED: 'Zoho refused the connection. Reconnect to Zoho.', // new — needs native review
      ZOHO_ACCESS_DENIED: 'The connected Zoho user has no access to this org or department.', // new — needs native review
      ZOHO_NOT_FOUND: 'Zoho could not find the org, department, ticket or contact.', // new — needs native review
      ZOHO_RATE_LIMITED: 'Too many requests to Zoho. Wait, then try again.', // new — needs native review
      ZOHO_UNAVAILABLE: 'Zoho Desk is unavailable right now. Try again later.', // new — needs native review
      ZOHO_REQUEST_REJECTED: 'Zoho refused the request, for example a field value it does not accept.', // new — needs native review
      ZOHO_INVALID_RESPONSE: 'Zoho answered with something unexpected.', // new — needs native review
      ZOHO_MAPPINGS_INVALID: 'The current field mappings cannot make a ticket. Check them in the form builder.', // new — needs native review
      ZOHO_BRANCH_NOT_MAPPED: 'The visit’s form maps no Zoho field to the branch.', // new — needs native review
      ZOHO_UNEXPECTED: 'Unexpected error with Zoho.', // new — needs native review
    },
    issue: {
      default: 'This value is not valid.',
      invalid_type: 'Required, or the wrong kind of value.',
      too_small: 'Too short or too small.',
      too_big: 'Too long or too large.',
      invalid_format: 'The format is not valid.',
      invalid_value: 'This value is not one of the allowed options.',
      not_multiple_of: 'This number is not allowed.',
      unrecognized_keys: 'Contains unexpected fields.',
      invalid_union: 'This value is not valid.',
      custom: 'This value is not valid.',
    },
  },
  ...formsEn,
  ...importEn,
  ...analyticsEn,
  ...trackingEn,
  ...catalogEn,
  ...adminEn,
};
